import { useRef } from "react";

import { ChartDataset } from "chart.js";

import { postTrackingData } from "@/api/endpoints/track";
import Line from "@/components/Charts/Line";
import { ANALYSIS_DATA_TYPE, WIDGET_FILTER } from "@/constants";
import { useI18n } from "@/contexts/i18nContext";
import { EVENT } from "@/tracking_events";
import ClickHandlerPlugin from "@components/Charts/plugins/ClickHandlerPlugin";
import IndexPlugin from "@components/Charts/plugins/IndexPlugin";
import ReactLegendPlugin from "@components/Charts/plugins/ReactLegendPlugin";
import { dynamicIntervalLabels } from "@components/Charts/utils/dynamicIntervalLabels";
import prepareDatasets from "@components/Charts/utils/prepareDataset";

import { BaseWidget } from "../BaseWidget";
import { ReachInfoLink } from "../ReachWidget";
import CheckboxLegend from "./partials/CheckboxLegend";
import {
  PackedWidgetData,
  reactLegendOnClickForSoundWithIndex,
  tooltipWithDateRangeCallback,
  unpackWidgetData,
} from "./utils";
import { BaseWidgetProps, WidgetProps } from "./WidgetProps";

type ChartDataType = ChartDataset<"line", number[]>;
type ReachData = PackedWidgetData<ChartDataType>;
type ReachWidgetProps = WidgetProps<ReachData>;
export type ExternalReachWidgetProps = BaseWidgetProps & ReachWidgetProps;

const ReachOverTime: React.FunctionComponent<ExternalReachWidgetProps> = ({
  ...props
}) => {
  const { data, filters } = props;
  const chartData = prepareDatasets(unpackWidgetData<ChartDataType>(data));

  const legendRef = useRef<HTMLDivElement>(null);

  const { formatDate } = useI18n();

  const hasData = data?.some(({ results }) =>
    results?.dataset?.some(({ data }) => data?.some((d: number) => d > 0))
  );

  return (
    <BaseWidget
      // The types here should be fixed within BaseWidget. That is out of scope for now.
      {...props}
      hasData={hasData}
      title={L.REPORTS_WIDGET_DEFAULT_TITLE_REACH_OVER_TIME_INSIGHTS}
      description={
        L.REPORTS_WIDGET_DEFAULT_DESCRIPTION_REACH_OVER_TIME_INSIGHTS
      }
      filterKey={WIDGET_FILTER.MENTION_MEDIA_CATEGORY}
      extraHeader={<ReachInfoLink />}
      toolRef={legendRef}
    >
      {chartData && (
        <Line
          options={{
            plugins: {
              click: {
                onClick: (appliedFilters) => {
                  postTrackingData(EVENT.WIDGET_CLICK, {
                    widget: props.metric,
                  });
                  props.fetchMentions_v2(appliedFilters);
                },
              },
              tooltip: {
                callbacks: {
                  title: tooltipWithDateRangeCallback(
                    chartData.labels,
                    data?.[0]?.meta?.time_period,
                    formatDate,
                    filters?.end_date
                  ),
                },
              },
              legend: {
                display: false,
              },
              indexPlugin: {
                type: ANALYSIS_DATA_TYPE.INDUSTRY_INDEX,
              },
              reactLegend: {
                container: legendRef,
                component: CheckboxLegend,
                hidden: [ANALYSIS_DATA_TYPE.INDUSTRY_INDEX],
                onClick: reactLegendOnClickForSoundWithIndex,
              },
            },
            scales: {
              y: {
                ticks: {
                  precision: 0, // forces integers
                },
                min: 0,
              },
              x: {
                type: "time",
                ticks: {
                  display: false,
                  source: "labels",
                },
                grid: {
                  drawTicks: true,
                },
                time: {
                  minUnit: "month",
                  tooltipFormat: "PP",
                },
              },
              x2: dynamicIntervalLabels(chartData.meta?.time_period),
            },
          }}
          data={chartData}
          plugins={[IndexPlugin, ReactLegendPlugin, ClickHandlerPlugin]}
        />
      )}
    </BaseWidget>
  );
};

export default ReachOverTime;
